import { useState } from "react";
import MiradasHero from "./components/MiradasHero";
import QuoteCardVertical from "./components/QuoteCardVertical";
import QuoteCardHorizontal from "./components/QuoteCardHorizontal";
import NewsQuoteCard from "./components/NewsQuoteCard";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { useHero } from "@/hooks/useHero";
import { heroContent } from "@/data/heroContent";
import { useEffect } from "react";

const heroFallback = heroContent.find((h) => h.page === "miradas")!;

const filtrosPill = ["Ordenar por: Fecha", "Autoridades y Desición", "Salud y Gestión", "Academia y Estudiantes", "Comunidad y Medios"];

interface CitaDB {
  id: number;
  texto: string;
  autor?: string;
  cargo?: string;
  sector?: string;
  fecha?: string;
  likes?: number;
  avatar?: string;
}

interface NoticiaDB {
  id: number;
  titulo: string;
  extracto?: string;
  imagen?: string;
  fecha?: string;
  comuna?: string;
  categoria?: string;
  segmento?: string;
}

export default function Miradas() {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(false);
  const { data: citas, loading: citasLoading } = useSupabaseData<CitaDB>("citas", []);
  const { data: noticias, loading: noticiasLoading } = useSupabaseData<NoticiaDB>("noticias", []);
  const { data: miradasHero } = useHero("miradas", heroFallback);

  // Update page title from hero seo_title
  useEffect(() => {
    const seoTitle = miradasHero?.seo_title ?? heroFallback.seoTitle ?? "Miradas | MásMed Tarapacá";
    if (seoTitle) {
      document.title = seoTitle;
    }
  }, [miradasHero]);

  const isLoading = citasLoading || noticiasLoading;

  return (
    <div>
      <MiradasHero />

      {/* Filtros superiores tipo pill */}
      <section id="citas" className="py-5 md:py-6 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center gap-2 md:gap-3 overflow-x-auto pb-1">
            {/* Botón sliders */}
            <button className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-white border border-masmed-gray-200 shadow-sm flex items-center justify-center text-masmed-gray-700 hover:border-primary hover:text-primary transition-all duration-200 flex-shrink-0 cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-equalizer-line text-lg"></i>
              </div>
            </button>

            {/* Filtros pill */}
            {filtrosPill.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(activeFilter === f ? null : f)}
                className={`inline-flex items-center gap-1.5 px-4 md:px-5 py-2 md:py-2.5 rounded-xl text-xs md:text-sm whitespace-nowrap border transition-all duration-200 cursor-pointer shadow-sm ${
                  activeFilter === f
                    ? "bg-primary text-white border-primary shadow-md"
                    : "bg-white text-masmed-gray-700 border-masmed-gray-200 hover:border-primary hover:text-primary"
                }`}
                style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
              >
                {f}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line"></i>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Últimas Citas */}
      <section className="py-10 md:py-14 lg:py-16 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          {/* Título centrado */}
          <h2 className="text-center text-2xl md:text-3xl lg:text-4xl text-masmed-gray-900 mb-8 md:mb-10 lg:mb-12">
            Últimas citas
          </h2>

          {/* Grilla de citas: hasta 3 verticales + hasta 3 horizontales */}
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                i <= 3 ? <QuoteCardVertical key={i} /> : <QuoteCardHorizontal key={i} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
              {/* Fila superior: 3 cards verticales */}
              {citas.slice(0, 3).map((cita) => (
                <QuoteCardVertical key={cita.id} cita={cita} />
              ))}
              {/* Fila inferior: 3 cards horizontales */}
              {citas.slice(3, 6).map((cita) => (
                <QuoteCardHorizontal key={cita.id} cita={cita} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Citas por Noticia */}
      <section className="py-10 md:py-14 lg:py-16 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
          {/* Título centrado */}
          <h2 className="text-center text-2xl md:text-3xl lg:text-4xl text-masmed-gray-900 mb-8 md:mb-10 lg:mb-12">
            Citas por noticia
          </h2>

          {/* 3 cards grandes verticales - ahora usa slice en lugar de índices fijos */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
              {[1, 2, 3].map((i) => <NewsQuoteCard key={i} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
              {citas.slice(0, 3).map((cita, i) => (
                <NewsQuoteCard key={cita.id} cita={cita} noticia={noticias[i]} />
              ))}
            </div>
          )}

          {/* Lista inferior: 4 noticias en 2 columnas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6 mt-8 md:mt-10 lg:mt-12">
            {noticias.slice(0, 4).map((n, i) => (
              <div key={i} className="flex items-start gap-3 py-3">
                <div className="w-1 h-12 bg-primary rounded-full flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-masmed-gray-900 text-sm md:text-base leading-snug"
                  style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>
                    {n.titulo}
                  </h4>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-masmed-gray-500 text-xs md:text-sm font-medium">
                      {n.comuna}
                    </span>
                    <span className="text-masmed-gray-400 text-xs md:text-sm">
                      {n.fecha}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Botón Ver más */}
          <div className="flex justify-end mt-6 md:mt-8">
            <button
              onClick={() => setShowMore(!showMore)}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm rounded-full transition-colors duration-200 cursor-pointer shadow-sm"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
            >
              {showMore ? "Ver menos" : "Ver más"}
              <div className="w-4 h-4 flex items-center justify-center">
                {showMore ? (
                  <i className="ri-arrow-up-line"></i>
                ) : (
                  <i className="ri-arrow-down-line"></i>
                )}
              </div>
            </button>
          </div>

          {/* Desplegable vertical */}
          {showMore && (
            <div className="mt-6 md:mt-8 border border-masmed-gray-200 rounded-xl bg-white overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                {noticias.slice(4, 10).map((n, i) => (
                  <div key={i} className={`flex items-start gap-3 py-3 px-4 md:px-5 ${i % 2 === 0 ? 'md:border-r border-masmed-gray-100' : ''} ${i < 4 ? 'border-b border-masmed-gray-100' : ''}`}>
                    <div className="w-1 h-12 bg-primary rounded-full flex-shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-masmed-gray-900 text-sm md:text-base leading-snug"
                      style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>
                        {n.titulo}
                      </h4>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-masmed-gray-500 text-xs md:text-sm font-medium">
                          {n.comuna}
                        </span>
                        <span className="text-masmed-gray-400 text-xs md:text-sm">
                          {n.fecha}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}